import { useEffect, useMemo, useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {
  CloudRain, Cloud, Wind, Thermometer, Satellite, Zap,
  Snowflake, Waves, Info, MapPin, ExternalLink,
} from "lucide-react";

// Radar products backed by Windy's public embed. Each option maps to a
// combination of `overlay` (main layer) and `product` (model source) params.
// Windy's radar overlay = base reflectivity worldwide (composite blend);
// there's no public interactive "velocity" product in a free embed, so we
// surface the closest analog (wind flow) and link out to RadarScope for
// true Doppler velocity work.
const PRODUCTS = [
  {
    id: "radar",
    label: "Reflectivity",
    icon: CloudRain,
    overlay: "radar",
    product: "radar",
    description: "Live NEXRAD composite reflectivity — precip intensity in dBZ. Updates every 5–10 min.",
  },
  {
    id: "rain",
    label: "Precip · Forecast",
    icon: CloudRain,
    overlay: "rain",
    product: "ecmwf",
    description: "ECMWF forecast rain accumulation — scrub the timeline to see the next 10 days.",
  },
  {
    id: "hrrr",
    label: "HRRR Sim Reflectivity",
    icon: Zap,
    overlay: "radar",
    product: "hrrr",
    description: "HRRR 3-km simulated composite reflectivity — hour-by-hour storm forecast for the next 18 hours.",
  },
  {
    id: "wind",
    label: "Wind Flow",
    icon: Wind,
    overlay: "wind",
    product: "ecmwf",
    description: "Animated ECMWF wind streamlines at the surface — closest analog to base velocity in a free embed.",
  },
  {
    id: "gust",
    label: "Wind Gusts",
    icon: Wind,
    overlay: "gust",
    product: "ecmwf",
    description: "ECMWF surface gust forecast — critical for tracking severe outflow winds.",
  },
  {
    id: "temp",
    label: "Temperature",
    icon: Thermometer,
    overlay: "temp",
    product: "ecmwf",
    description: "2-meter air temperature forecast — watch cold-front timing across the state.",
  },
  {
    id: "thunder",
    label: "Thunderstorms",
    icon: Zap,
    overlay: "thunder",
    product: "ecmwf",
    description: "Lightning-density forecast — where the models expect electrified storms.",
  },
  {
    id: "clouds",
    label: "Cloud Cover",
    icon: Cloud,
    overlay: "clouds",
    product: "ecmwf",
    description: "Total cloud cover forecast — useful for sky-cover briefings and aviation.",
  },
  {
    id: "satellite",
    label: "Satellite",
    icon: Satellite,
    overlay: "satellite",
    product: "satellite",
    description: "GOES-East visible/IR satellite imagery — updates every 10 min from NOAA.",
  },
  {
    id: "snow",
    label: "Snowfall",
    icon: Snowflake,
    overlay: "snow",
    product: "ecmwf",
    description: "ECMWF snowfall accumulation forecast — mostly relevant to North Georgia in winter.",
  },
  {
    id: "waves",
    label: "Coastal Waves",
    icon: Waves,
    overlay: "waves",
    product: "ecmwfWaves",
    description: "Wave-height forecast off the Georgia coast — key for tropical setups and mariners.",
  },
];

const NEXRAD_SITES = [
  { id: "KFFC", name: "Atlanta", desc: "Peachtree City · covers Metro Atlanta + North GA" },
  { id: "KJGX", name: "Robins / Macon", desc: "Robins AFB · covers Middle & Central Georgia" },
  { id: "KVAX", name: "Moody AFB", desc: "Moody AFB · covers South Georgia + Valdosta" },
  { id: "KCLX", name: "Charleston", desc: "Charleston SC · overlaps Coastal Georgia" },
];

function buildWindyUrl({ overlay, product }) {
  // Center on middle-Georgia for a clean statewide framing.
  const params = new URLSearchParams({
    lat: "32.9",
    lon: "-83.5",
    detailLat: "32.9",
    detailLon: "-83.5",
    width: "1600",
    height: "900",
    zoom: "6",
    level: "surface",
    overlay,
    product,
    menu: "",
    message: "true",
    marker: "",
    calendar: "now",
    pressure: "",
    type: "map",
    location: "coordinates",
    detail: "",
    metricWind: "mph",
    metricTemp: "°F",
    radarRange: "-1",
  });
  return `https://embed.windy.com/embed2.html?${params.toString()}`;
}

export default function RadarPage() {
  const [activeId, setActiveId] = useState("radar");
  const active = useMemo(() => PRODUCTS.find((p) => p.id === activeId) || PRODUCTS[0], [activeId]);
  const iframeSrc = useMemo(() => buildWindyUrl(active), [active]);
  const [siteId, setSiteId] = useState("KFFC");
  // Windy uses WebGL which can degrade after many hours on a 24/7 kiosk.
  // Force a fresh reload every 20 min so the WebGL context never rots overnight.
  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setReloadKey((k) => k + 1), 20 * 60 * 1000);
    return () => clearInterval(t);
  }, []);
  // Cache-busting bump so the static GIF re-fetches when React remounts —
  // updated once per minute so the loop feels live without hammering NOAA.
  const bust = Math.floor(Date.now() / 60_000);

  return (
    <div className="relative min-h-screen">
      <Header />

      <section className="relative py-8 sm:py-10" data-testid="radar-page">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          {/* Title */}
          <div className="mb-4">
            <div className="font-oswald text-xs uppercase tracking-[0.3em] text-[#1f63c9]">
              Interactive Radar & Model Products
            </div>
            <h1 className="mt-1 font-oswald text-3xl font-bold uppercase tracking-wider text-white sm:text-4xl lg:text-5xl">
              Georgia Radar Room
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-gray-400">
              Pan, zoom, scrub the timeline, and switch products. Base reflectivity is live NEXRAD; forecast layers pull ECMWF and HRRR runs.
            </p>
          </div>

          {/* Product tabs */}
          <div
            data-testid="radar-product-tabs"
            className="mb-3 flex snap-x snap-mandatory gap-2 overflow-x-auto pb-2 scrollbar-hide"
          >
            {PRODUCTS.map((p) => {
              const Icon = p.icon;
              const isActive = p.id === activeId;
              return (
                <button
                  key={p.id}
                  data-testid={`radar-tab-${p.id}`}
                  onClick={() => setActiveId(p.id)}
                  className={`snap-start whitespace-nowrap rounded-sm border px-3 py-2 font-oswald text-xs font-bold uppercase tracking-widest transition-colors ${
                    isActive
                      ? "border-[#1f63c9] bg-[#1f63c9]/20 text-white"
                      : "border-white/10 bg-black/40 text-gray-300 hover:border-white/30 hover:text-white"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <Icon className="h-3.5 w-3.5" />
                    {p.label}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Description strip */}
          <div className="mb-3 flex items-start gap-2 rounded-sm border border-white/10 bg-black/40 p-3">
            <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#ffd60a]" />
            <div>
              <div className="font-oswald text-xs uppercase tracking-widest text-[#ffd60a]">
                {active.label}
              </div>
              <p className="text-sm text-gray-300">{active.description}</p>
            </div>
          </div>

          {/* Interactive embed */}
          <div
            className="relative aspect-video overflow-hidden rounded border border-white/10 bg-black shadow-[0_20px_60px_rgba(0,0,0,0.6)]"
            data-testid="radar-embed"
          >
            <iframe
              key={`${iframeSrc}-${reloadKey}`}
              title={`Georgia Radar - ${active.label}`}
              src={`${iframeSrc}&_r=${reloadKey}`}
              className="absolute inset-0 h-full w-full"
              allow="fullscreen"
              sandbox="allow-scripts allow-same-origin allow-popups"
              loading="eager"
            />
          </div>

          {/* Georgia NEXRAD Sites — live NWS RIDGE loops per site */}
          <section className="mt-10" data-testid="nexrad-sites">
            <div className="mb-4 flex items-end justify-between flex-wrap gap-2">
              <div>
                <div className="font-oswald text-xs uppercase tracking-[0.3em] text-[#1f63c9]">
                  Georgia NEXRAD Sites
                </div>
                <h2 className="mt-1 font-oswald text-2xl font-bold uppercase tracking-wider text-white sm:text-3xl">
                  Site-Specific Radar Loops
                </h2>
                <p className="mt-1 text-sm text-gray-400">
                  Live NWS composite reflectivity loops for the four NEXRAD sites that cover Georgia. Click any tile to open the full interactive station map with product switching (base velocity, storm-relative motion, precipitation, and more).
                </p>
              </div>
            </div>

            {/* 4-up grid of loops */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {NEXRAD_SITES.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setSiteId(s.id)}
                  data-testid={`nexrad-tile-${s.id}`}
                  className={`group relative overflow-hidden rounded-sm border bg-black text-left transition-colors ${
                    siteId === s.id ? "border-[#1f63c9]" : "border-white/10 hover:border-white/30"
                  }`}
                >
                  <div className="relative aspect-square overflow-hidden">
                    <img
                      src={`https://radar.weather.gov/ridge/standard/${s.id}_loop.gif?v=${bust}`}
                      alt={`${s.id} radar loop`}
                      className="absolute inset-0 h-full w-full object-cover"
                      loading="eager"
                    />
                    {/* label overlay */}
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/95 via-black/60 to-transparent p-2">
                      <div className="flex items-center gap-1.5">
                        <MapPin className="h-3 w-3 text-[#ffd60a]" />
                        <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-[#ffd60a]">
                          {s.id}
                        </span>
                        <span className="font-oswald text-[10px] font-bold uppercase tracking-widest text-white">
                          {s.name}
                        </span>
                      </div>
                    </div>
                    <div className="pointer-events-none absolute inset-x-0 top-0 flex items-center gap-1 bg-black/60 px-2 py-1 backdrop-blur-sm">
                      <span className="live-dot" style={{ width: 5, height: 5 }} />
                      <span className="font-oswald text-[9px] font-bold uppercase tracking-widest text-white">Live · NWS RIDGE</span>
                    </div>
                  </div>
                  <div className="border-t border-white/5 p-2">
                    <p className="text-[11px] text-gray-400">{s.desc}</p>
                  </div>
                </button>
              ))}
            </div>

            {/* Interactive drill-down */}
            <div className="mt-4">
              <div className="mb-2 flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-[#ffd60a]" />
                  <div className="font-oswald text-sm font-bold uppercase tracking-widest text-white">
                    {siteId} · {NEXRAD_SITES.find((x) => x.id === siteId)?.name} — Interactive
                  </div>
                </div>
                <a
                  href={`https://radar.weather.gov/?station=${siteId}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 rounded-sm border border-white/15 px-3 py-1.5 font-oswald text-[10px] font-bold uppercase tracking-widest text-white hover:border-[#1f63c9] hover:text-[#1f63c9]"
                >
                  <ExternalLink className="h-3 w-3" /> Open Full NWS Site
                </a>
              </div>
              <p className="mb-3 text-xs text-gray-400">
                The embedded NWS interactive lets you switch products (Base Reflectivity, Base Velocity, Storm Relative Motion, Precipitation) via the layer menu on the left, and scrub the timeline at the bottom.
              </p>
              <div
                className="relative aspect-video overflow-hidden rounded border border-white/10 bg-black shadow-[0_20px_60px_rgba(0,0,0,0.6)]"
                data-testid="nexrad-interactive"
              >
                <iframe
                  key={siteId}
                  title={`NWS ${siteId} Interactive Radar`}
                  src={`https://radar.weather.gov/?station=${siteId}`}
                  className="absolute inset-0 h-full w-full"
                  allow="fullscreen"
                  sandbox="allow-scripts allow-same-origin allow-popups"
                  loading="lazy"
                />
              </div>
            </div>
          </section>

          {/* Meteorologist notes / external tools */}
          <div className="mt-4 grid gap-3 md:grid-cols-3">
            <div className="rounded-sm border border-white/10 bg-black/40 p-4">
              <div className="font-oswald text-xs uppercase tracking-widest text-[#ffd60a]">Pro Tools</div>
              <p className="mt-1 text-sm text-gray-300">
                For true Doppler velocity, storm-relative velocity, and correlation coefficient work,{" "}
                <a
                  href="https://www.rainviewer.com/desktop-radar.html"
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#ffd60a] underline hover:text-white"
                >
                  RainViewer Desktop
                </a>{" "}
                (free) or{" "}
                <a
                  href="https://www.radarscope.com/"
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#ffd60a] underline hover:text-white"
                >
                  RadarScope
                </a>{" "}
                (paid) are the industry standard.
              </p>
            </div>
            <div className="rounded-sm border border-white/10 bg-black/40 p-4">
              <div className="font-oswald text-xs uppercase tracking-widest text-[#ffd60a]">Georgia Radar Sites</div>
              <p className="mt-1 text-sm text-gray-300">
                <span className="font-mono text-white">KFFC</span> Atlanta ·{" "}
                <span className="font-mono text-white">KJGX</span> Robins/Macon ·{" "}
                <span className="font-mono text-white">KVAX</span> Moody AFB ·{" "}
                <span className="font-mono text-white">KCLX</span> Charleston covers Coastal GA.
              </p>
            </div>
            <div className="rounded-sm border border-white/10 bg-black/40 p-4">
              <div className="font-oswald text-xs uppercase tracking-widest text-[#ffd60a]">Timeline Trick</div>
              <p className="mt-1 text-sm text-gray-300">
                Click the play button (bottom-left of the map) to animate the loop. Use the scrubber to jump ahead 24, 48, or 72 hours on any forecast layer.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
